/**
 * lib/pupilLedger.ts
 * ------------------------------------------------------------------
 * ONE PUPIL'S ACCOUNT, TRANSACTION BY TRANSACTION.
 *
 * The general ledger answers what the school is owed in total. A
 * statement answers what a FAMILY owes — which is right for parents,
 * because siblings are billed together, and wrong the moment somebody
 * asks what this particular child has been charged and paid. Between
 * those two there was nowhere to look.
 *
 * THE DISCOUNT IS ITS OWN LINE, NOT NETTED INTO THE CHARGE. An invoice
 * is charged at its SUBTOTAL and anything given away appears beneath it
 * as a credit. Netting them saves a row and destroys the answer to "what
 * did the school forgo, and to whom" — permanently, because once the
 * charge is stored net there is nothing left to recover it from. Subtotal
 * less discount less receipts still equals the invoice balance, so the
 * account ties either way; only the visibility differs.
 *
 * DATED WHEN IT HAPPENED, NOT WHEN IT WAS KEYED. A receipt sits on the
 * day the money arrived and an adjustment on the day it was decided. A
 * ledger ordered by capture date is a ledger nobody can reconcile against
 * a bank statement.
 *
 * COMPUTED, NEVER STORED — the same rule the general ledger follows. A
 * stored balance drifts from the entries behind it, silently, and then
 * the account and its own history disagree with nothing to say which is
 * right.
 *
 * PURE. Documents in, an account out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { Invoice, Payment } from '@/lib/financeCore';
import type { Adjustment, AdjustmentKind } from '@/lib/adjustments';

export type PupilEntryKind =
  | 'invoice' | 'discount' | 'receipt' | 'credit_note' | 'write_off' | 'refund';

export const PUPIL_ENTRY_LABEL: Record<PupilEntryKind, string> = {
  invoice: 'Invoice',
  discount: 'Discount',
  receipt: 'Receipt',
  credit_note: 'Credit note',
  write_off: 'Written off',
  refund: 'Refund',
};

export interface PupilLedgerEntry {
  id: string;
  date: string;
  kind: PupilEntryKind;
  /** The document's own number — an invoice no, a receipt no, ADJ/…. */
  reference: string;
  description: string;
  /** What was added to what the pupil owes. */
  charge: number;
  /** What was taken off it — a receipt, a discount, a credit note. */
  credit: number;
  balance: number;
}

export interface PupilLedger {
  entries: PupilLedgerEntry[];
  charged: number;
  discounted: number;
  received: number;
  adjusted: number;
  /** What the pupil still owes. The closing balance, by another route. */
  outstanding: number;
}

/**
 * A refund gives money BACK to a family, so it increases what they owe
 * again; the other two kinds reduce it. Direction comes from the kind and
 * never from a sign, because a negative "amount" is the classic way a
 * credit silently becomes a charge.
 */
const ADJUSTMENT_IS_CHARGE: Record<AdjustmentKind, boolean> = {
  credit_note: false,
  write_off: false,
  refund: true,
};

/** Build one pupil's account, oldest first, with a running balance. */
export function buildPupilLedger(
  studentId: string,
  invoices: Invoice[],
  payments: Payment[],
  adjustments: Adjustment[] = [],
): PupilLedger {
  const raw: Omit<PupilLedgerEntry, 'balance'>[] = [];

  for (const inv of invoices) {
    if (inv.studentId !== studentId) continue;
    if (inv.status === 'void') continue;

    const subtotal = round2(Number(inv.subtotal) || 0);
    raw.push({
      id: `inv_${inv.id}`,
      date: (inv.issuedAt ?? '').slice(0, 10),
      kind: 'invoice',
      reference: inv.invoiceNo ?? inv.id,
      description: `${inv.term ?? ''} ${inv.year ?? ''}`.trim() || 'Fees',
      charge: subtotal,
      credit: 0,
    });

    /* Its own line, dated with the invoice. What was given away is a
       question the school has to be able to answer. */
    const discount = round2(Number(inv.discountTotal) || 0);
    if (discount > 0) {
      const names = (inv.discounts ?? []).map((d) => d.label).filter(Boolean);
      raw.push({
        id: `dsc_${inv.id}`,
        date: (inv.issuedAt ?? '').slice(0, 10),
        kind: 'discount',
        reference: inv.invoiceNo ?? inv.id,
        description: names.length ? names.join(', ') : 'Discount',
        charge: 0,
        credit: discount,
      });
    }
  }

  for (const p of payments) {
    if (p.studentId !== studentId) continue;
    raw.push({
      id: `pay_${p.id}`,
      date: (p.receivedAt ?? '').slice(0, 10),
      kind: 'receipt',
      reference: p.receiptNo ?? p.reference ?? p.id,
      description: p.note?.trim() || 'Payment received',
      charge: 0,
      credit: round2(Number(p.amount) || 0),
    });
  }

  for (const a of adjustments) {
    if (a.studentId !== studentId) continue;
    if (a.reversedBy) continue;
    const amount = round2(Number(a.amount) || 0);
    const isCharge = ADJUSTMENT_IS_CHARGE[a.kind] ?? false;
    raw.push({
      id: `adj_${a.id}`,
      date: (a.date ?? a.createdAt ?? '').slice(0, 10),
      kind: a.kind,
      reference: a.adjustmentNo ?? a.id,
      description: a.reason || PUPIL_ENTRY_LABEL[a.kind],
      charge: isCharge ? amount : 0,
      credit: isCharge ? 0 : amount,
    });
  }

  /* Oldest first. Where two land on one day, the charge is read before
     what settles it — a receipt shown above the invoice it pays makes the
     balance look wrong for one line, and that is the line somebody rings
     about. */
  raw.sort((a, b) => a.date.localeCompare(b.date)
    || (b.charge - a.charge)
    || a.reference.localeCompare(b.reference));

  let running = 0;
  const entries: PupilLedgerEntry[] = raw.map((e) => {
    running = round2(running + e.charge - e.credit);
    return { ...e, balance: running };
  });

  const sum = (k: PupilEntryKind, field: 'charge' | 'credit') =>
    round2(raw.filter((e) => e.kind === k).reduce((n, e) => n + e[field], 0));

  return {
    entries,
    charged: sum('invoice', 'charge'),
    discounted: sum('discount', 'credit'),
    received: sum('receipt', 'credit'),
    adjusted: round2(
      sum('credit_note', 'credit') + sum('write_off', 'credit') - sum('refund', 'charge'),
    ),
    outstanding: running,
  };
}

/**
 * Does the account agree with the invoices it was built from?
 *
 * The sub-ledger and the invoice records are two routes to the same
 * number, and a difference means one of them is wrong. Worth stating on
 * the screen rather than leaving somebody to notice: this is precisely
 * the reconciliation an auditor performs by hand.
 */
export function pupilLedgerTies(
  ledger: PupilLedger,
  invoices: Invoice[],
  studentId: string,
): { ties: boolean; ledger: number; invoices: number; difference: number } {
  const fromInvoices = round2(invoices
    .filter((i) => i.studentId === studentId && i.status !== 'void')
    .reduce((n, i) => n + (Number(i.balance) || 0), 0));

  const difference = round2(ledger.outstanding - fromInvoices);
  return {
    ties: Math.abs(difference) < 0.005,
    ledger: ledger.outstanding,
    invoices: fromInvoices,
    difference,
  };
}
